// src/blog/BlogIndex.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../seo/Seo";
import { client } from "../sanity/client";

type BlogItem = {
  _id: string;
  title?: string;
  slug?: { current?: string };
  excerpt?: string;
  date?: string;
  publishedAt?: string;
  _createdAt?: string;
};

const BLOG_INDEX_QUERY = `
  *[_type == "post" && defined(slug.current)]
  | order(coalesce(publishedAt, date, _createdAt) desc) {
    _id,
    title,
    slug,
    excerpt,
    date,
    publishedAt,
    _createdAt
  }
`;

export default function BlogIndex() {
  const [posts, setPosts] = useState<BlogItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client
      .fetch<BlogItem[]>(BLOG_INDEX_QUERY)
      .then((rows) => {
        if (!alive) return;
        setPosts(rows || []);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  return (
    <main className="mx-auto w-full max-w-5xl px-6 py-16">
      <Seo
        title="Blog – Babblebots.ai"
        description="Insights on AI hiring, screening, interviews, and faster recruitment with Babblebots."
        url="/blog"
        type="website"
      />

      <h1 className="text-3xl font-semibold text-white">Blog</h1>
      <p className="mt-2 max-w-2xl text-gray-300">
        Guides, playbooks, and product insights from Babblebots.
      </p>

      <div className="mt-10 space-y-4">
        {loading ? (
          <div className="text-gray-400">Loading posts…</div>
        ) : posts.length === 0 ? (
          <div className="text-gray-400">No posts found.</div>
        ) : (
          posts.map((p) => {
            const s = p?.slug?.current;
            if (!s) return null;

            return (
              <article
                key={p._id}
                className="rounded-xl border border-gray-800 bg-gray-900/40 p-6"
              >
                <Link
                  to={`/blog/${s}`}
                  className="text-xl font-semibold text-white hover:underline"
                >
                  {p.title || "Untitled"}
                </Link>

                {p.excerpt ? (
                  <p className="mt-2 text-gray-300">{p.excerpt}</p>
                ) : null}

                <div className="mt-4">
                  <Link
                    to={`/blog/${s}`}
                    className="text-sm font-medium text-gray-200 hover:underline"
                  >
                    Read more →
                  </Link>
                </div>
              </article>
            );
          })
        )}
      </div>
    </main>
  );
}
